import { getGlobalHeadersAsObject } from "../global-headers";
import { ResolvedRoleData, RoleDescriptionDC } from "./assignment-llm-skills";
import type { ApplicantReplyDrafts } from "./job-ad";

/** A published Jobylon job the signed-in user's customer has not imported yet. */
export interface JobylonImportableJob {
	/** ODI's row id — what the preview call takes. */
	id: number;
	/** Jobylon's own job id, shown so it can be matched against their UI. */
	job_id: number;
	title: string;
	company: string | null;
	locations: string[];
	ad_url: string | null;
	last_seen_in_feed: string | null;
}

/**
 * A Jobylon job in the same shape the role wizard's review step consumes.
 *
 * Deliberately identical to `previewRoleExtraction`'s response minus the LLM
 * parts: Jobylon states the title, company and locations outright, so there is
 * nothing to extract and no debug or template data to return.
 */
export interface JobylonJobPreview {
	role_structured: RoleDescriptionDC;
	resolved: ResolvedRoleData;
	jobylon_job_id: number;
}

export const fetchJobylonImportableJobs = async (
	signal?: AbortSignal
): Promise<JobylonImportableJob[]> => {
	const response = await fetch("/api/v2/jobylon-jobs/", {
		headers: getGlobalHeadersAsObject(),
		signal,
	});
	if (!response.ok) {
		throw new Error(`Could not load Jobylon jobs (${response.status})`);
	}
	return response.json();
};

/** One Jobylon application held for a person, and where it stands. */
export interface JobylonRole {
	/** ODI's assignment for that job, null for a job never linked to a role. */
	assignment: number | null;
	title: string;
	/** Empty while ODI holds the stage id and has not read the pipeline. */
	stage: string;
	/** Rejected or hired — the customer is finished with this application. */
	is_closed: boolean;
	origin: "applied" | "sourced";
	/** The customer's own words for why they turned this one down; "" if they
	 *  gave none, and for everyone still live. */
	reason: string;
	board_url: string;
}

/** Jobylon applications for these people, keyed by person id. */
export type JobylonPresence = Map<number, JobylonRole[]>;

/**
 * Ask which of *personIds* this customer's Jobylon already holds.
 *
 * One call for a whole page of people: the callers are lists — search results,
 * a grading queue — and a call per row would be a request per name on screen.
 */
export const fetchJobylonPresence = async (
	personIds: number[],
	signal?: AbortSignal
): Promise<JobylonPresence> => {
	const query = personIds.map((id) => `person=${id}`).join("&");
	const response = await fetch(`/api/v2/jobylon-jobs/presence/?${query}`, {
		headers: getGlobalHeadersAsObject(),
		signal,
	});
	if (!response.ok) {
		throw new Error(`Could not read Jobylon candidates (${response.status})`);
	}
	const body = (await response.json()) as Record<string, JobylonRole[]>;
	return new Map(
		Object.entries(body).map(([personId, roles]) => [Number(personId), roles])
	);
};

/**
 * Re-read the customer's feed now and return what it holds.
 *
 * The scheduled poll runs every 15 minutes; this is for the recruiter who has
 * just published a job in Jobylon and does not want to wait for it.
 */
export const refreshJobylonJobs = async (): Promise<JobylonImportableJob[]> => {
	const response = await fetch("/api/v2/jobylon-jobs/refresh/", {
		method: "POST",
		headers: getGlobalHeadersAsObject(),
	});
	const body = await response.json().catch(() => null);
	if (!response.ok) {
		throw new Error(body?.detail ?? `Could not check Jobylon (${response.status})`);
	}
	return body;
};

/**
 * Record that a Jobylon job became this assignment.
 *
 * The backend refuses a second link, so a job can only ever be imported once —
 * a 409 means someone got there first, and carries the assignment they made.
 *
 * `applicantReplies` are the yes and no the recruiter reviewed in the wizard.
 * They are stored by the same request, before the job points at the role:
 * its applicants are queued for grading only once the role can answer them.
 */
export const linkJobylonJobToAssignment = async (
	id: number,
	assignmentId: number,
	applicantReplies?: ApplicantReplyDrafts
): Promise<{ assignment: number }> => {
	const response = await fetch(`/api/v2/jobylon-jobs/${id}/link/`, {
		method: "POST",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify({
			assignment: assignmentId,
			applicant_replies: applicantReplies,
		}),
	});
	if (!response.ok) {
		const detail = await response.json().catch(() => null);
		throw new Error(
			detail?.detail ?? `Could not link the Jobylon job (${response.status})`
		);
	}
	return response.json();
};

/** A Jobylon job that has already become an ODI assignment. */
export interface JobylonLinkedJob {
	id: number;
	job_id: number;
	assignment: number;
	/** This job's candidate board in Jobylon's own UI. */
	board_url: string;
}

/** One stage of a Jobylon job's pipeline, as far as ODI has observed it. */
export interface JobylonStage {
	status_id: number;
	name: string;
	group: number | null;
}

/**
 * The customer's Jobylon jobs that are already linked to an assignment.
 *
 * Used to tell which roles have their real pipeline in Jobylon, so a person
 * added to one is pushed there rather than only onto ODI's own board.
 */
export const fetchJobylonLinkedJobs = async (
	signal?: AbortSignal
): Promise<JobylonLinkedJob[]> => {
	const response = await fetch("/api/v2/jobylon-jobs/linked/", {
		headers: getGlobalHeadersAsObject(),
		signal,
	});
	if (!response.ok) {
		throw new Error(`Could not load linked Jobylon jobs (${response.status})`);
	}
	return response.json();
};

/**
 * A Jobylon job's pipeline stages.
 *
 * Read through to the customer's Jobylon feed on every call, so the recruiter
 * picks from the pipeline as it stands rather than a stale copy. Answers 502
 * when Jobylon is unreachable.
 */
export const fetchJobylonStages = async (
	id: number,
	signal?: AbortSignal
): Promise<JobylonStage[]> => {
	const response = await fetch(`/api/v2/jobylon-jobs/${id}/stages/`, {
		headers: getGlobalHeadersAsObject(),
		signal,
	});
	if (!response.ok) {
		throw new Error(`Could not load Jobylon stages (${response.status})`);
	}
	return response.json();
};

/** Put a person on a Jobylon job's board as a sourced candidate. */
export const pushPersonToJobylonJob = async (
	id: number,
	personId: number,
	statusId?: number
): Promise<{ application_id: number; status_id: number | null }> => {
	const response = await fetch(`/api/v2/jobylon-jobs/${id}/push/`, {
		method: "POST",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ person: personId, status_id: statusId }),
	});
	const body = await response.json().catch(() => null);
	if (!response.ok) {
		throw new Error(
			body?.detail ?? `Could not push to Jobylon (${response.status})`
		);
	}
	return body;
};

/**
 * The Jobylon candidate list for one job, narrowed to one person.
 *
 * `board_url` already carries the job and `status_group=all`; this adds the
 * list's own search term. Jobylon exposes no per-application page — its Push
 * API answers with a status id and nothing else, no name and no URL — so
 * searching the board by name is the only way to land on one candidate.
 *
 * Empty in, empty out, so a role that is not Jobylon's stays unlinked.
 */
export const jobylonCandidateUrl = (
	boardUrl: string | undefined,
	personName: string | undefined
): string => {
	if (!boardUrl) return "";
	if (!personName) return boardUrl;
	return `${boardUrl}&q=${encodeURIComponent(personName)}`;
};

/**
 * Move a candidate to another stage of their Jobylon job, whether ODI pushed
 * them or they applied through the customer's own form.
 *
 * Answers 404 when Jobylon holds no application for them on that job and 409
 * when ODI holds no Push credentials for the customer.
 */
export const moveJobylonCandidate = async (
	id: number,
	personId: number,
	statusId: number
): Promise<{ status_id: number }> => {
	const response = await fetch(`/api/v2/jobylon-jobs/${id}/move/`, {
		method: "POST",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ person: personId, status_id: statusId }),
	});
	const body = await response.json().catch(() => null);
	if (!response.ok) {
		throw new Error(
			body?.detail ?? `Could not move in Jobylon (${response.status})`
		);
	}
	return body;
};

export const fetchJobylonJobPreview = async (
	id: number,
	signal?: AbortSignal
): Promise<JobylonJobPreview> => {
	const response = await fetch(`/api/v2/jobylon-jobs/${id}/preview/`, {
		headers: getGlobalHeadersAsObject(),
		signal,
	});
	if (!response.ok) {
		throw new Error(`Could not load that Jobylon job (${response.status})`);
	}
	return response.json();
};
